/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { Fragment } from 'react'

import { EuiSkeletonText, EuiSpacer } from '@elastic/eui'

import { isMarketplaceCustomer, isTrial } from '@modules/billing-lib/billingDetails/utils'
import useBillingDetails from '@modules/billing-lib/billingDetails/useBillingDetails'
import withBillingNotificationsCenter from '@modules/billing-components/BillingNotificationsCenter/withBillingNotificationsCenter'
import ErrorCallout from '@modules/billing-components/ErrorCallout'
import useOrganizationId from '@modules/billing-lib/hooks/useOrganizationId'

import MarketplacesOverview from './MarketplacesOverview'
import DirectOverview from './DirectOverview'
import TrialOverview from './TrialOverview'

import type { FunctionComponent } from 'react'

const BillingOverview: FunctionComponent = () => {
  const organizationId = useOrganizationId()
  const billingDetails = useBillingDetails()

  if (billingDetails.error) {
    return (
      <Fragment>
        <EuiSpacer size='xxl' />
        <ErrorCallout onReload={billingDetails.refetch} />
      </Fragment>
    )
  }

  if (!organizationId || !billingDetails.data) {
    return <EuiSkeletonText />
  }

  if (isTrial(billingDetails.data)) {
    return <TrialOverview organizationId={organizationId} />
  }

  if (isMarketplaceCustomer(billingDetails.data)) {
    return <MarketplacesOverview organizationId={organizationId} />
  }

  return <DirectOverview organizationId={organizationId} />
}

export default withBillingNotificationsCenter(BillingOverview)
